/**
 * Pure model helpers for the Prompt Studio (lot B step 2).
 * No React, no API calls, no side effects.
 *
 * @see docs/plans/prompt-studio-build/B-popup-frontend.md §Files and props
 */

import {
  placeKey,
  promptPlaceKey,
  effectiveZoneMeta,
  validateZoneMeta,
} from '@traaker/shared';
import type { TFunction } from 'i18next';
import type {
  BrandQuery,
  BrandQueryAxis,
  AllowanceFigures,
  AllowanceKind,
  FirstRunNotice,
  PromptLastRun,
} from '@traaker/shared';
import type { TargetZone, Location, GeoSuggestion } from '@traaker/shared';

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

/**
 * PROMPT_ZONE_EDITING shipped false; lot C step 9 flipped it (free zones).
 * While false: the opened prompt's place trigger opens Pick with Mondial +
 * the row's place + the brand's Location zones only (no conversation zone,
 * no « Autres lieux », no autocomplete call), single-select, saved by
 * PATCH { zoneMeta }; the composer and review place stay read-only « Mondial »,
 * Couverture « + Ajouter une localisation » is hidden and only Mondial hatched
 * cells open the composer.
 */
export const PROMPT_ZONE_EDITING = true;

/**
 * AXIS_SPLIT_LIVE: true since the ship-4 flip (2026-11-03), the same change that deleted the
 * axis page's per-axis create dialog (B step 13); Comparaison prompts run on the new pipeline
 * only (no EventBridge schedule), so a tenant still on 'legacy' cannot measure them.
 * It gates what OFFERS `comparison` (AxisPicker dots, Générer axis chips,
 * Couverture segments, lot I's turn-1 mapping) and every LaunchDialog mount.
 * Lists, groups and facets follow the rows: a row already on `comparison`
 * is always listed (C0).
 */
export const AXIS_SPLIT_LIVE = true;

// ---------------------------------------------------------------------------
// Axis ordering
// ---------------------------------------------------------------------------

/**
 * THE one axis order (C9; UI-SPEC §2.2: Comparaison last):
 * list groups, Couverture segments, AxisPicker dots, first-run lines,
 * launch progress, fitsMargin. Never a hand-written three-axis list.
 */
export const AXIS_ORDER: readonly BrandQueryAxis[] = [
  'narrative', 'authority', 'referral', 'comparison',
] as const;

/**
 * Axes offered to the user — AXIS_ORDER minus 'comparison' while !live.
 * Callers pass AXIS_SPLIT_LIVE; tests pass both values.
 */
export function offeredAxes(live: boolean): readonly BrandQueryAxis[] {
  return live ? AXIS_ORDER : AXIS_ORDER.filter((a) => a !== 'comparison');
}

/** UI-SPEC §5.2 axis dot colours. */
export const AXIS_COLOR: Record<BrandQueryAxis, string> = {
  narrative:  '#6d28d9',
  authority:  '#0f766e',
  referral:   '#be185d',
  comparison: '#1d4ed8',
};

// ---------------------------------------------------------------------------
// Place key (LocKey)
// ---------------------------------------------------------------------------

/**
 * LocKey = C1 placeKey(q, brand.locations) result, verbatim:
 *   `p1:…`           = promptPlaceKey(effective zone)
 *   `legacy:<…>`     = unresolved legacy zone, NEVER Mondial
 *   '∅'              = Mondial
 *
 * B adds no key of its own.
 */
export type LocKey = string;

/** Sentinel key for Mondial (no zone). */
export const MONDIAL: LocKey = '∅';

// ---------------------------------------------------------------------------
// Family types
// ---------------------------------------------------------------------------

/** Rows grouped by familyId ?? queryId (C1). */
export interface Family {
  root: BrandQuery;
  rows: BrandQuery[];   // root + variants
}

/** loc/ax/tp triple for Couverture. tp null = (aucun). */
export interface CellTriple {
  loc: LocKey;
  ax: BrandQueryAxis;
  tp: string | null;
}

// ---------------------------------------------------------------------------
// locKeyOf / isClassified
// ---------------------------------------------------------------------------

/** Place key of a prompt row: = placeKey(q, locations), nothing else. */
export function locKeyOf(
  q: BrandQuery,
  locations: readonly Location[] | undefined,
): LocKey {
  return placeKey(q, locations);
}

/**
 * A row is classified when it has a real axis and a classifiedAt timestamp.
 * A row with `axis === 'pending'` is never classified.
 */
export function isClassified(q: BrandQuery): boolean {
  return q.axis !== 'pending' && q.classifiedAt !== undefined;
}

// ---------------------------------------------------------------------------
// knownZones / promptPickerZones (C14)
// ---------------------------------------------------------------------------

/**
 * C14 — all zones known to the studio, keyed by promptPlaceKey.
 *
 * Sources:
 *   - each row's effectiveZoneMeta (lot A helpers)
 *   - conversationZones passed from lot I ([] until then)
 *   - each Location as effectiveZoneMeta({ zone: l.zone || l.label }, [l])
 *
 * @param rows         all brand queries loaded by the studio
 * @param convZones    zones from conversation personas (lot I; pass [] for now)
 * @param locations    brand.locations
 */
export function knownZones(
  rows: BrandQuery[],
  convZones: TargetZone[],
  locations: readonly Location[],
): Map<LocKey, TargetZone> {
  const out = new Map<LocKey, TargetZone>();

  // Zones from prompt rows
  for (const q of rows) {
    const z = effectiveZoneMeta(q, locations);
    if (z) {
      const k = promptPlaceKey(z);
      if (!out.has(k)) out.set(k, z);
    }
  }

  // Zones from conversation personas (lot I; empty for now)
  for (const z of convZones) {
    const k = promptPlaceKey(z);
    if (!out.has(k)) out.set(k, z);
  }

  // Zones derived from brand Locations
  for (const loc of locations) {
    const synthetic: { zoneMeta?: TargetZone; zone?: string } = {
      zone: loc.zone || loc.label,
    };
    const z = effectiveZoneMeta(synthetic, [loc]);
    if (z) {
      const k = promptPlaceKey(z);
      if (!out.has(k)) out.set(k, z);
    }
  }

  return out;
}

/**
 * C14 R19 — zones listed by every PROMPT zone picker (composer, opened prompt,
 * lot E).
 *
 * While PROMPT_ZONE_EDITING is false: Location zones only (C14 R19: lot C's S21
 * refuses any other zone until step 9).
 * When true: all `known` zones.
 */
export function promptPickerZones(
  known: Map<LocKey, TargetZone>,
  locations: readonly Location[],
): Map<LocKey, TargetZone> {
  if (PROMPT_ZONE_EDITING) return known;

  // Derive Location zones only
  const out = new Map<LocKey, TargetZone>();
  for (const loc of locations) {
    const synthetic: { zoneMeta?: TargetZone; zone?: string } = {
      zone: loc.zone || loc.label,
    };
    const z = effectiveZoneMeta(synthetic, [loc]);
    if (z) {
      const k = promptPlaceKey(z);
      if (!out.has(k)) out.set(k, z);
    }
  }
  return out;
}

/**
 * A place picker's listed zones: `first` (the current place, a pending pick;
 * null / undefined skipped) then `listed`, deduped by promptPlaceKey.
 */
export function placeOptionsOf(
  first: ReadonlyArray<TargetZone | null | undefined>,
  listed: Map<LocKey, TargetZone>,
): Map<LocKey, TargetZone> {
  const out = new Map<LocKey, TargetZone>();
  for (const z of first) {
    if (z && !out.has(promptPlaceKey(z))) out.set(promptPlaceKey(z), z);
  }
  for (const [k, z] of listed) {
    if (!out.has(k)) out.set(k, z);
  }
  return out;
}

/**
 * Place search results (GET /locations/autocomplete) → clean TargetZones, ≤ `max`.
 * A suggestion `validateZoneMeta` refuses (no 2-letter countryCode, bad type,
 * no slug) is dropped: a country is never derived from a name.
 */
export function suggestionZones(suggestions: readonly GeoSuggestion[], max = 4): TargetZone[] {
  const out = new Map<LocKey, TargetZone>();
  for (const s of suggestions) {
    let z: TargetZone;
    try {
      z = validateZoneMeta(s);
    } catch {
      continue;
    }
    const k = promptPlaceKey(z);
    if (!out.has(k)) out.set(k, z);
    if (out.size >= max) break;
  }
  return [...out.values()];
}

// ---------------------------------------------------------------------------
// groupFamilies
// ---------------------------------------------------------------------------

/**
 * Group a flat list of BrandQuery rows into Family objects.
 * Rows sharing the same familyId belong together; standalone rows (no familyId)
 * form their own single-row family keyed by queryId.
 */
export function groupFamilies(qs: BrandQuery[]): Family[] {
  const map = new Map<string, BrandQuery[]>();
  for (const q of qs) {
    const key = q.familyId ?? q.queryId;
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(q);
    } else {
      map.set(key, [q]);
    }
  }

  const families: Family[] = [];
  for (const [key, members] of map) {
    // Root is the row whose queryId equals the family key (i.e. has no familyId)
    const root = members.find((q) => q.queryId === key) ?? members[0];
    families.push({ root, rows: members });
  }
  return families;
}

// ---------------------------------------------------------------------------
// List groups
// ---------------------------------------------------------------------------

/**
 * Group key for the prompt list section headers.
 *   pending      — un-classified rows (axis 'pending')
 *   narrative    — Image axis active rows
 *   authority    — Autorité axis active rows
 *   referral     — Recommandation axis active rows
 *   comparison   — Comparaison axis active rows
 *   disabled     — archived root, no archivedReason or 'user'
 *   paused       — archived root, archivedReason 'plan_limit'
 */
export type ListGroupKey = 'pending' | BrandQueryAxis | 'disabled' | 'paused';

/**
 * Build the prompt list groups from a set of families.
 *
 * Order: pending, then AXIS_ORDER, then disabled, then paused.
 * Empty groups are omitted.
 *
 * Membership rule (C1 R2, reads the archived ROOT's archivedReason):
 *   - root.status 'archived', reason absent or 'user' → disabled
 *   - root.status 'archived', reason 'plan_limit'      → paused
 *   - root.status 'archived', reason 'deleted'         → never shown (dropped at load)
 *   - active root on 'pending' axis                    → pending
 *   - active root on a real axis                       → that axis's group
 */
export function listGroups(
  fs: Family[],
): Array<{ key: ListGroupKey; families: Family[] }> {
  const buckets: Record<ListGroupKey, Family[]> = {
    pending:    [],
    narrative:  [],
    authority:  [],
    referral:   [],
    comparison: [],
    disabled:   [],
    paused:     [],
  };

  for (const f of fs) {
    const r = f.root;
    if (r.status === 'archived') {
      if (r.archivedReason === 'plan_limit') {
        buckets.paused.push(f);
      } else if (r.archivedReason === 'deleted') {
        // dropped — never shown
      } else {
        // absent or 'user'
        buckets.disabled.push(f);
      }
    } else {
      // active
      if (r.axis === 'pending') {
        buckets.pending.push(f);
      } else {
        buckets[r.axis].push(f);
      }
    }
  }

  const order: ListGroupKey[] = ['pending', ...AXIS_ORDER, 'disabled', 'paused'];
  const result: Array<{ key: ListGroupKey; families: Family[] }> = [];
  for (const key of order) {
    if (buckets[key].length > 0) {
      result.push({ key, families: buckets[key] });
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// promptsCount / familyShape
// ---------------------------------------------------------------------------

/** Active row count for a family. null when root.axis === 'pending'. */
export function promptsCount(f: Family): number | null {
  if (f.root.axis === 'pending') return null;
  return f.rows.filter((q) => q.status === 'active').length;
}

/**
 * Grid shape of a family: how many distinct languages, places, and holes.
 *
 *   langs  = distinct `language` values (absent counts as one unknown value)
 *   places = distinct locKeyOf
 *   holes  = (langs × places) – active rows count
 *
 * FamilyToken shows iff langs × places > 1; its pale square iff holes > 0.
 * 'deleted' rows are not loaded, so a removed language or place leaves the grid.
 */
export function familyShape(
  f: Family,
  locations: readonly Location[] | undefined,
): { langs: number; places: number; holes: number } {
  // langs and places are computed over ALL loaded rows (deleted never load;
  // archived rows keep their lang/place in the grid — it's a "hole").
  const langSet  = new Set<string>();
  const placeSet = new Set<LocKey>();
  for (const q of f.rows) {
    langSet.add(q.language ?? '__unknown__');
    placeSet.add(locKeyOf(q, locations));
  }

  const langs  = langSet.size  || 1;
  const places = placeSet.size || 1;

  // pairSet: (lang × place) cells that have at least one ACTIVE row
  const pairSet = new Set<string>();
  for (const q of f.rows.filter((r) => r.status === 'active')) {
    pairSet.add(`${q.language ?? '__unknown__'}\0${locKeyOf(q, locations)}`);
  }

  // holes = total grid cells minus filled cells
  const holes = Math.max(0, langs * places - pairSet.size);

  return { langs, places, holes };
}

// ---------------------------------------------------------------------------
// FirstRunNotice helpers
// ---------------------------------------------------------------------------

/**
 * Display-merge of ONE save's responses (UI-SPEC §2.26).
 * One entry per (axis, when), count and cost added,
 * the earliest nextLaunchAt kept (absent when none has one).
 * `b` absent → `a`; never decides a `when`.
 */
export function mergeFirstRun(
  a: FirstRunNotice[] | null,
  b: FirstRunNotice[] | undefined,
): FirstRunNotice[] | null {
  if (!b || b.length === 0) return (a && a.length > 0) ? a : null;
  const base = a ?? [];

  const map = new Map<string, FirstRunNotice>();
  for (const entry of base) {
    map.set(`${entry.axis}|${entry.when}`, { ...entry });
  }
  for (const entry of b) {
    const k = `${entry.axis}|${entry.when}`;
    const existing = map.get(k);
    if (existing) {
      existing.count += entry.count;
      existing.cost  += entry.cost;
      // Keep the earliest nextLaunchAt
      if (entry.nextLaunchAt) {
        if (!existing.nextLaunchAt || entry.nextLaunchAt < existing.nextLaunchAt) {
          existing.nextLaunchAt = entry.nextLaunchAt;
        }
      }
    } else {
      map.set(k, { ...entry });
    }
  }

  const result = Array.from(map.values());
  return result.length > 0 ? result : null;
}

/**
 * Render lines for a FirstRunNotice array, ordered by AXIS_ORDER.
 *
 * Returns one entry per (axis, when):
 *   key 'promptStudio.firstRun.now' | '.next' | '.nextNoDate'
 *   (i18next adds _one / _other from count)
 *   date = nextLaunchAt as JJ/MM, formatted like appliesFrom
 *
 * [] for null / [].
 */
export function firstRunLines(
  e: FirstRunNotice[] | null,
): Array<{ key: string; axis: BrandQueryAxis; count: number; date?: string }> {
  if (!e || e.length === 0) return [];

  const result: Array<{ key: string; axis: BrandQueryAxis; count: number; date?: string }> = [];

  for (const ax of AXIS_ORDER) {
    const nowEntry  = e.find((x) => x.axis === ax && x.when === 'now');
    const nextEntry = e.find((x) => x.axis === ax && x.when === 'next_launch');

    if (nowEntry) {
      result.push({ key: 'promptStudio.firstRun.now', axis: ax, count: nowEntry.count });
    }
    if (nextEntry) {
      let date: string | undefined;
      if (nextEntry.nextLaunchAt) {
        const d = new Date(nextEntry.nextLaunchAt);
        date = `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      }
      result.push({
        key: date ? 'promptStudio.firstRun.next' : 'promptStudio.firstRun.nextNoDate',
        axis: ax,
        count: nextEntry.count,
        date,
      });
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// AllowanceFigures extended (fitsByAxis is server-computed, not yet in the type)
// ---------------------------------------------------------------------------

/**
 * AllowanceFigures extended with promptRequests-only fields returned by
 * GET /brands/:b/allowances (00b DQ11).
 * We extend rather than re-declare to stay in sync with the shared type.
 */
export type PromptAllowanceFigures = AllowanceFigures & {
  fitsByAxis?: Partial<Record<BrandQueryAxis, number>>;
};

// ---------------------------------------------------------------------------
// fitsMargin
// ---------------------------------------------------------------------------

/**
 * The self-serve « il reste N prompts » (00b DQ11 fitsByAxis, server-computed).
 *
 * With `axis` (the cost line of an opened family, and the composer while
 * Générer is the source: its axis) lo = hi = fitsByAxis[axis].
 *
 * Without (the composer, axis unknown before classification):
 *   lo / hi = min / max over offeredAxes(AXIS_SPLIT_LIVE).
 *
 * null when fig is null, allowance is -1 or fitsByAxis is absent.
 * hi ≤ 0 greys « Vérifier » (00b DQ11).
 */
export function fitsMargin(
  fig: PromptAllowanceFigures | null,
  axis?: BrandQueryAxis,
): { lo: number; hi: number } | null {
  if (!fig) return null;
  if (fig.allowance === -1) return null;
  if (!fig.fitsByAxis) return null;

  if (axis !== undefined) {
    const n = fig.fitsByAxis[axis];
    if (n === undefined) return null;
    return { lo: n, hi: n };
  }

  // No axis: min/max over offered axes
  const axes = offeredAxes(AXIS_SPLIT_LIVE);
  let lo = Infinity;
  let hi = -Infinity;
  for (const ax of axes) {
    const n = fig.fitsByAxis[ax];
    if (n !== undefined) {
      if (n < lo) lo = n;
      if (n > hi) hi = n;
    }
  }
  if (!isFinite(lo) || !isFinite(hi)) return null;
  return { lo, hi };
}

// ---------------------------------------------------------------------------
// limitReason / needRowsReason / leftLines / NO_ROOM / structuralRowError
// ---------------------------------------------------------------------------

/** A greyed control's reason — key + interpolation values for i18n. */
export type LimitSlot = { key: string; values: Record<string, number | string> };

/**
 * THE wording of a self-serve refusal (00b DQ11), shared by B, D, E, G and I.
 * Reads the refused change's figures (figuresAfter of the preview or of the 409 body).
 *
 *   sustainableNeed > allowance → promptStudio.fit.overQuota { need, cap, unit }
 *   monthNeed > remaining       → promptStudio.fit.overMonth { need, left, unit }
 *   neither                     → null
 */
export function limitReason(
  after: AllowanceFigures,
  kind: AllowanceKind,
): LimitSlot | null {
  const unit = kind === 'promptRequests'
    ? 'promptStudio.quota.unitRequests'
    : 'promptStudio.quota.unitRuns';

  if (after.sustainableNeed > after.allowance) {
    return {
      key: 'promptStudio.fit.overQuota',
      values: { need: after.sustainableNeed, cap: after.allowance, unit },
    };
  }
  if (after.monthNeed > after.remaining) {
    return {
      key: 'promptStudio.fit.overMonth',
      values: { need: after.monthNeed, left: after.remaining, unit },
    };
  }
  return null;
}

/**
/**
 * Interpolate a LimitSlot's i18n key with its values.
 * Shared by PausedRow, LimitReasonLine and ReviewTable (UI-SPEC §6.2).
 * `unit` and `axis` values are themselves i18n keys and are resolved first.
 */
export function slotText(t: TFunction, slot: LimitSlot): string {
  const values: Record<string, unknown> = { ...slot.values };
  if (typeof values.unit === 'string') values.unit = t(values.unit as string);
  if (typeof values.axis === 'string') values.axis = t(`promptStudio.axis.${values.axis as string}`);
  return t(slot.key, values as Record<string, string | number>);
}

/**
 * The BEFORE-a-click greying of UI-SPEC §2.4b / §2.6 / §2.11.
 *
 * added > current.fitsByAxis[axis] → prompt.fit.needRows_one/_other { count, n, axis }
 * null when current is null, fitsByAxis absent (allowance -1, agency) or added fits.
 */
export function needRowsReason(
  added: number,
  current: PromptAllowanceFigures | null,
  axis: BrandQueryAxis,
): LimitSlot | null {
  if (!current) return null;
  if (!current.fitsByAxis) return null;
  const n = current.fitsByAxis[axis];
  if (n === undefined) return null;
  if (added <= n) return null;
  return {
    key: added === 1 ? 'promptStudio.fit.needRows_one' : 'promptStudio.fit.needRows_other',
    values: { count: added, n, axis },
  };
}

/**
 * « Il reste {n} prompts {axis}. » per axis of a refused batch.
 * AXIS_ORDER; current = the 409 body's figures or GET .../allowances.
 * [] when fitsByAxis is absent.
 */
export function leftLines(
  current: PromptAllowanceFigures | null,
  axes: BrandQueryAxis[],
): LimitSlot[] {
  if (!current?.fitsByAxis) return [];
  const result: LimitSlot[] = [];
  for (const ax of AXIS_ORDER) {
    if (!axes.includes(ax)) continue;
    const n = current.fitsByAxis[ax];
    if (n === undefined) continue;
    result.push({
      key: n <= 0 ? 'promptStudio.fit.leftAxisNone' : 'promptStudio.fit.leftAxis',
      values: { n, count: n, axis: ax }, // count: i18next picks fit.leftAxis_one / _other
    });
  }
  return result;
}

/** « Vérifier » greyed: no axis has room (UI-SPEC §2.12). */
export const NO_ROOM: LimitSlot = { key: 'promptStudio.fit.noRoom', values: {} };

/**
 * Per-row / 400 codes that are a structural cap, never a quota (C3, 00b DQ14).
 * Returns null for anything else (the server's message is shown).
 */
export function structuralRowError(
  code: string,
  axis: BrandQueryAxis | 'pending',
  on: 'save' | 'reactivate',
): LimitSlot | null {
  if (code === 'BRAND_PROMPT_LIMIT') {
    return {
      key: on === 'save'
        ? 'promptStudio.brandLimit.row'
        : 'promptStudio.brandLimit.reactivate',
      values: {},
    };
  }
  if (code === 'AXIS_PROMPT_LIMIT') {
    return {
      key: on === 'save'
        ? 'promptStudio.axisLimit.row'
        : 'promptStudio.axisLimit.reactivate',
      values: { axis },
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// lastRunOf / lastRunLabel
// ---------------------------------------------------------------------------

/**
 * Aggregated last-run info for a family.
 *
 * Over the family's ACTIVE rows that have a lastRuns entry:
 *   okDay   = the latest (by okAt)
 *   notToday = the union of their enginesNotToday (ALL_ENGINES order)
 *   n        = the family's active rows
 *
 * null when runs is null or no active row has an entry.
 */
export function lastRunOf(
  f: Family,
  runs: Record<string, PromptLastRun> | null,
): { okDay?: string; notToday: string[]; n: number } | null {
  if (!runs) return null;

  const active = f.rows.filter((q) => q.status === 'active');
  const n = active.length;

  let latestOkAt: string | undefined;
  let latestOkDay: string | undefined;
  const notTodaySet = new Set<string>();

  let found = false;
  for (const q of active) {
    const r = runs[q.queryId];
    if (!r) continue;
    found = true;

    // Latest okAt → corresponding okDay
    if (r.okAt) {
      if (!latestOkAt || r.okAt > latestOkAt) {
        latestOkAt  = r.okAt;
        latestOkDay = r.okDay;
      }
    }
    for (const e of r.enginesNotToday) {
      notTodaySet.add(e);
    }
  }

  if (!found) return null;
  return { okDay: latestOkDay, notToday: Array.from(notTodaySet), n };
}

// ---------------------------------------------------------------------------
// Batch Désactiver / Supprimer (list selection)
// ---------------------------------------------------------------------------

export type BatchOp = 'disable' | 'delete';

export interface ArchiveCall {
  axis: BrandQueryAxis | 'pending';
  queryId: string;
  reason?: 'deleted';
}

/**
 * The archive calls of one selected family, in order — the opened prompt's own:
 * Désactiver = the root only, no reason (PromptRowExpanded `handleDisable`);
 * Supprimer = every variant then the root, `reason: 'deleted'` (`handleDelete`).
 */
export function batchCalls(f: Family, op: BatchOp): ArchiveCall[] {
  const call = (q: BrandQuery): ArchiveCall => ({
    axis: q.axis as BrandQueryAxis | 'pending',
    queryId: q.queryId,
    ...(op === 'delete' ? { reason: 'deleted' as const } : {}),
  });
  if (op === 'disable') return [call(f.root)];
  return [...f.rows.filter((r) => r.queryId !== f.root.queryId), f.root].map(call);
}

/**
 * The header « select all » over the rows on screen (after search / filters):
 * all of them already selected → clear them, else add them. Selected rows that
 * are filtered out stay as they are (the batch acts only on visible ones).
 */
export function toggleAllVisible(selected: ReadonlySet<string>, visibleIds: readonly string[]): Set<string> {
  const next = new Set(selected);
  const allOn = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  for (const id of visibleIds) {
    if (allOn) next.delete(id);
    else next.add(id);
  }
  return next;
}

/**
 * Runs the families one after the other (a family stops at its first failed
 * call). Returns the root ids of the families that failed.
 */
export async function runBatch(
  families: readonly Family[],
  op: BatchOp,
  archive: (c: ArchiveCall) => Promise<unknown>,
  onProgress?: (done: number) => void,
): Promise<string[]> {
  const failed: string[] = [];
  for (let i = 0; i < families.length; i++) {
    try {
      for (const c of batchCalls(families[i], op)) await archive(c);
    } catch {
      failed.push(families[i].root.queryId);
    }
    onProgress?.(i + 1);
  }
  return failed;
}

/**
 * UI-SPEC §2.28 label for a last run day.
 *
 * okDay = todayUtc → 'today'
 * the day before   → 'yesterday'
 * older            → 'date' + JJ/MM (or JJ/MM/AA past a year)
 *
 * todayUtc = new Date().toISOString().slice(0, 10).
 */
export function lastRunLabel(
  okDay: string,
  todayUtc: string,
): { key: 'today' | 'yesterday' | 'date'; date?: string } {
  if (okDay === todayUtc) return { key: 'today' };

  // "yesterday" = todayUtc minus one day
  const yesterday = new Date(todayUtc + 'T00:00:00Z');
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const yStr = yesterday.toISOString().slice(0, 10);
  if (okDay === yStr) return { key: 'yesterday' };

  // Older: format as JJ/MM, or JJ/MM/AA if more than a year ago
  const [y, m, d] = okDay.split('-').map(Number);
  const [ty, , ] = todayUtc.split('-').map(Number);
  const dayStr = `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
  const date = (ty - y) >= 1
    ? `${dayStr}/${String(y).slice(2)}`
    : dayStr;
  return { key: 'date', date };
}
